import React from 'react';
import type { CardData, AddressData } from '../steps';
import { ApiHandler, useDonorProfile } from '../../../api';
import type { DonorWithWallet } from '../../../api/models/Donor';
import { checkCardType } from '../../../utils/cardUtils';
import type { Wallet } from '../../../api/models/Donor';
import { requestRecaptchaV3Token } from '../../Wrappers/ReCaptchaV3';

export type AddCardStep = 'card2' | 'address' | 'address2';

const getInitialAddress = (
    data: DonorWithWallet | undefined,
): AddressData => ({
    street: data?.donor.address.street || '',
    city: data?.donor.address.city || '',
    state: data?.donor.address.state || '',
    zip: data?.donor.address.zip || '',
    countryCode: data?.donor.address.country.isoAlpha2 || 'US',
});

export const useAddCardModal = (
    onCloseCallback: () => void,
    onAddWalletCallback?: (wallet: Wallet) => void,
) => {
    const { donorProfile } = useDonorProfile();

    const initialAddress = React.useMemo(
        () => getInitialAddress(donorProfile),
        [donorProfile],
    );

    const [step, setStep] = React.useState<AddCardStep>('card2');
    const [isLoading, setIsLoading] = React.useState(false);
    const [card, setCard] = React.useState<CardData | undefined>(undefined);
    const [address, setAddress] = React.useState<AddressData>(initialAddress);
    const [stepError, setStepError] = React.useState<string | null>(null);

    const dismissError = React.useCallback(() => setStepError(null), []);

    const resetModal = React.useCallback(() => {
        setStep('card2');
        setCard(undefined);
        setAddress(initialAddress);
        setStepError(null);
        setIsLoading(false);
    }, [initialAddress]);

    const resetModalAndClose = React.useCallback(() => {
        resetModal();
        onCloseCallback();
    }, [onCloseCallback, resetModal]);

    const handleClose = React.useCallback(() => {
        if (!isLoading) {
            resetModalAndClose();
        }
    }, [isLoading, resetModalAndClose]);

    const onCardSubmit = React.useCallback((cardData: CardData) => {
        setCard(cardData);
        setStepError(null);
        setStep('address');
    }, []);

    const onAddressSubmit = React.useCallback((addressData: AddressData) => {
        setAddress(addressData);
        setStepError(null);
        setStep('address2');
    }, []);

    const onAddress2Submit = React.useCallback(
        (city: string, state: string) => {
            const mergedAddress: AddressData = { ...address, city, state };
            setAddress(mergedAddress);
            setStepError(null);
            setIsLoading(true);

            const user = {
                name: card?.nameOnCard,
                card,
                address: mergedAddress,
            };

            requestRecaptchaV3Token(
                async (token: string | undefined) => {
                    const request =
                        await ApiHandler.instance.gift.addCardRequest(
                            user,
                            token,
                        );

                    if (!request.success) {
                        setStep('card2');
                        setStepError(request.error.message);
                        setIsLoading(false);
                        return;
                    }

                    const newWallet: Wallet = {
                        id: request.data.response.payload.walletId,
                        number: user.card?.cardNumber.slice(-4) || '',
                        paymentInstrumentUuid:
                            request.data.response.payload.paymentInstrumentId,
                        type:
                            (checkCardType(user.card?.cardNumber || '')
                                ?.type as Wallet['type']) || 'Visa',
                        isPreferred: false,
                    };

                    onAddWalletCallback?.(newWallet);
                    resetModalAndClose();
                },
                undefined,
                'AddCard',
            );
        },
        [address, card, onAddWalletCallback, resetModalAndClose],
    );

    const onBack = React.useMemo(() => {
        if (step === 'card2') return undefined;
        if (step === 'address') return () => setStep('card2');
        return () => setStep('address'); // address2
    }, [step]);

    return {
        step,
        isLoading,
        savedCard: card,
        savedAddress: address,
        stepError,
        dismissError,
        onCardSubmit,
        onAddressSubmit,
        onAddress2Submit,
        onBack,
        handleClose,
    };
};
